// New consents (intake) data layer — Blueprint §2 screen 2 (F-INT-*). Ports the
// SEMANTICS of legacy loadIntake / renderIntake / checkDuplicate + the promote
// handshake's honest result parsing (v20 lines ~1808-1867, 5338-5352, 5476-5511)
// onto the 2.0 store + envelope + fetchAll infrastructure.
//
// The pure classification (status normalization, 48h SLA/overdue, No-PDF flag,
// duplicate detection, tab bucketing, promote-result parsing + honest toast copy)
// lives here as unit-tested functions with no DB and no DOM. loadIntake() is the
// only impure part: it reads the consent_submission VIEW (+ a light patients read
// for cross-table duplicate detection) and publishes SLICES.INTAKE.
//
// COMPAT (Rule 2): consent_submission is a VIEW keyed on submission_id with
// programme_id (NOT programme_code); status is the lowercase DB enum. promote is
// done by the DEPLOYED promote_consent_to_case RPC — this module only PARSES its
// result shape, never changes how it is called.

import { store, SLICES } from '../state/store.js';
import { getClient, fetchAll, applyProgrammeFilter, ok, fail } from '../db/client.js';
import { TABLES, BUCKETS } from '../db/contract.js';

// SLA window: a pending consent is "overdue" once it has waited longer than this
// since submission (legacy: created_at + 48h < now).
export const INTAKE_SLA_HOURS = 48;

// ─── Status normalization ──────────────────────────────────────────────────

// The DB enum (lowercase) → the display label. Legacy normalised with a blunt
// charAt-upper which turned 'on_hold' into 'On_hold'; we map known enums to
// clean labels while keeping the SAME pending/processed partition (only
// 'pending' is New/active — everything else is Processed).
const STATUS_LABELS = Object.freeze({
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
  on_hold: 'On hold',
  promoted: 'Promoted',
});

/**
 * Normalize a raw consent status to its display label. Unknown values get a
 * best-effort title-case (legacy fallback) so nothing renders as a raw enum.
 * @param {any} raw consent_submission.status (lowercase DB enum)
 * @returns {string}
 */
export function normalizeStatus(raw) {
  const s = String(raw || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (STATUS_LABELS[s]) return STATUS_LABELS[s];
  const t = String(raw || '').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : '';
}

/**
 * Is this a still-pending consent (the only status shown in the active tabs)?
 * Everything else (approved/declined/on_hold/promoted) is "Processed".
 * @param {any} sub raw or enriched submission (reads .status)
 * @returns {boolean}
 */
export function isPending(sub) {
  return String(sub && sub.status || '').trim().toLowerCase() === 'pending';
}

/**
 * A promote is a no-op only when the consent is ALREADY promoted (legacy
 * double-promote guard: On Hold/Declined stay acceptable, so only 'promoted'
 * blocks). Normalises spaces/dashes so 'On Hold' never trips it.
 * @param {any} status
 * @returns {boolean}
 */
export function isAlreadyPromoted(status) {
  return String(status || '').trim().toLowerCase().replace(/[\s-]+/g, '_') === 'promoted';
}

/**
 * Is this consent marked declined? (drives the promote-over-declined confirm.)
 * @param {any} status
 * @returns {boolean}
 */
export function isDeclined(status) {
  return String(status || '').trim().toLowerCase().replace(/[\s-]+/g, '_') === 'declined';
}

// ─── SLA / overdue + No-PDF ─────────────────────────────────────────────────

/**
 * Overdue = a PENDING consent whose submission age exceeds INTAKE_SLA_HOURS
 * (legacy: dueAt = created_at + 48h; overdue when now > dueAt). A non-pending
 * row is never "overdue" (it has already been actioned).
 * @param {any} sub reads .status + .created_at
 * @param {Date} now
 * @returns {boolean}
 */
export function isOverdue(sub, now) {
  if (!isPending(sub)) return false;
  const created = new Date(sub && sub.created_at);
  if (isNaN(created.getTime())) return false;
  const dueAt = created.getTime() + INTAKE_SLA_HOURS * 3600 * 1000;
  return now.getTime() > dueAt;
}

/**
 * A PENDING consent with no signed consent PDF on file (legacy No-PDF tab +
 * '⚠ No signed PDF' badge). The scanned document must be attached before a
 * promote lands cleanly.
 * @param {any} sub reads .status + .signed_pdf_url
 * @returns {boolean}
 */
export function hasNoPdf(sub) {
  return isPending(sub) && !(sub && sub.signed_pdf_url);
}

// ─── Stale-intake watchdog (client half) ───────────────────────────────────
// Why this exists: 29 real patients' signed consents sat unactioned for up to
// SEVEN MONTHS before go-live. The per-row "overdue" chips were there the whole
// time — what was missing was one loud, un-ignorable summary. The DB half is a
// daily pg_cron job (migration 20260802140000) writing an audit event; this is
// the in-your-face banner on the New consents view.

/** Days a consent may wait without a decision before it counts as STALE. */
export const INTAKE_STALE_DAYS = 7;

/**
 * Pure: summarize consents that have waited more than `days` without a
 * decision. Counts BOTH 'pending' and 'on_hold' — deliberately wider than the
 * pending-only tab partition, because on-hold rows leave the New tab and that
 * is exactly how a January consent stayed invisible until August: "on hold"
 * must never quietly become "forgotten". Promoted/declined/approved rows have
 * been decided and never count.
 * @param {any[]} rows raw or enriched submissions (reads .status, .created_at, .patient_name)
 * @param {Date} [now] injectable clock
 * @param {number} [days] threshold, default INTAKE_STALE_DAYS
 * @returns {{n: number, oldestDays: number, oldestName: string}}
 */
export function staleIntake(rows, now, days = INTAKE_STALE_DAYS) {
  const base = now instanceof Date && !isNaN(now.getTime()) ? now.getTime() : Date.now();
  const cutoff = base - days * 86400 * 1000;
  let n = 0;
  let oldestT = Infinity;
  let oldestName = '';
  for (const r of rows || []) {
    const st = String((r && r.status) || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (st !== 'pending' && st !== 'on_hold') continue;
    const t = new Date(r && r.created_at).getTime();
    if (isNaN(t) || t >= cutoff) continue;
    n++;
    if (t < oldestT) {
      oldestT = t;
      oldestName = String((r && r.patient_name) || '').trim();
    }
  }
  return {
    n,
    oldestDays: n ? Math.floor((base - oldestT) / 86400000) : 0,
    oldestName,
  };
}

// ─── Duplicate detection ────────────────────────────────────────────────────

/**
 * Last 9 digits of a phone (legacy checkDuplicate key: national numbers written
 * with/without +254 / leading 0 still collide on their final 9 digits). '' when
 * there are fewer than 9 digits — too weak a signal to group on.
 * @param {any} phone
 * @returns {string}
 */
export function phoneKey(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 9 ? digits.slice(-9) : '';
}

/**
 * The set of phone keys that appear MORE THAN ONCE across the submission list —
 * every occurrence (incl. the first) is a member, so both halves of a duplicate
 * pair get the badge (legacy dupPhones semantics, keyed on last-9). Rows with
 * too few digits to key are ignored.
 * @param {any[]} rows
 * @returns {Set<string>}
 */
export function dupPhoneKeys(rows) {
  /** @type {Map<string, number>} */
  const counts = new Map();
  for (const r of rows || []) {
    const k = phoneKey(r && r.patient_phone);
    if (k) counts.set(k, (counts.get(k) || 0) + 1);
  }
  const dup = new Set();
  for (const [k, n] of counts) if (n > 1) dup.add(k);
  return dup;
}

/**
 * Cross-table duplicate check against existing patients/cases (legacy
 * checkDuplicate): same last-9 phone, else same national ID, else same name.
 * Returns the first match with a human reason, or null. Pure — pass the patient
 * list in (loadIntake reads it once).
 * @param {any} sub the submission
 * @param {any[]} patients existing case/patient rows (patient_name/phone/national_id)
 * @returns {{match: any, reason: string}|null}
 */
export function checkDuplicate(sub, patients) {
  if (!sub || !patients || !patients.length) return null;
  const phone = phoneKey(sub.patient_phone);
  const id = String(sub.national_id || '').trim().toLowerCase();
  const name = String(sub.patient_name || '').trim().toLowerCase();
  for (const c of patients) {
    const cPhone = phoneKey(c.patient_phone);
    const cId = String(c.national_id || '').trim().toLowerCase();
    const cName = String(c.patient_name || '').trim().toLowerCase();
    if (phone && cPhone && phone === cPhone) return { match: c, reason: 'Same phone number' };
    if (id && cId && id === cId) return { match: c, reason: 'Same national ID' };
    if (name && cName && name === cName) return { match: c, reason: 'Same name' };
  }
  return null;
}

// ─── R-025 evidence parity (bulk/manual entry) ──────────────────────────────
// NOTE (implemented per Blueprint §2 / R-025 — NOT present in legacy v_20):
// a consent that was entered by STAFF (manual "Add patient" or a bulk import)
// rather than signed on the PUBLIC form must carry scanned-consent evidence
// (the signed PDF) AND a named verifier before it can be promoted. Public-form
// submissions already carry the patient's own digital signature, so they are
// exempt. The staff-entered signal is the consent_actor_role / consent_version
// the writer stamps (manual entry sets consent_actor_role='nurse',
// consent_version='v1_manual'; bulk import stamps an import version). Adjust the
// predicate once the exact import-stamp value is confirmed on the live view.

const PUBLIC_ACTOR_ROLES = Object.freeze(['patient', 'self', 'guardian', 'caregiver']);

/**
 * Was this consent entered by staff (manual/bulk) rather than the public form?
 * @param {any} sub reads consent_actor_role + consent_version
 * @returns {boolean}
 */
export function isStaffEntered(sub) {
  const role = String(sub && sub.consent_actor_role || '').trim().toLowerCase();
  const ver = String(sub && sub.consent_version || '').trim().toLowerCase();
  if (/manual|import|legacy|bulk/.test(ver)) return true;
  if (role && !PUBLIC_ACTOR_ROLES.includes(role)) return true; // nurse/coordinator/staff/admin
  return false;
}

/**
 * R-025 gate: does promoting THIS consent require scanned-consent evidence +
 * a verifier name? True only for staff-entered rows (public-form consents are
 * exempt — they carry the patient's own signature).
 * @param {any} sub
 * @returns {boolean}
 */
export function requiresEvidence(sub) {
  return isStaffEntered(sub);
}

// ─── Classification (per-row flags + tab counts) ────────────────────────────

/**
 * One tab's id + label (exact legacy buckets). NOTE: the task brief paraphrases
 * these as "Pending / No PDF / Duplicates / On hold"; the legacy behavioural
 * spec's actual tabs are New / Overdue SLA / Duplicates / No PDF / Processed —
 * kept verbatim (on-hold consents live under Processed).
 */
export const INTAKE_TABS = Object.freeze([
  Object.freeze({ id: 'new', label: 'New', countKey: 'new' }),
  Object.freeze({ id: 'overdue', label: 'Overdue SLA', countKey: 'overdue' }),
  Object.freeze({ id: 'duplicates', label: 'Duplicates', countKey: 'dup' }),
  Object.freeze({ id: 'nopdf', label: 'No PDF', countKey: 'nopdf' }),
  Object.freeze({ id: 'processed', label: 'Processed', countKey: 'processed' }),
]);

/** An all-zero count block (used on a failed load so badges read 0, not stale). */
export function emptyIntakeCounts() {
  return { new: 0, overdue: 0, dup: 0, nopdf: 0, processed: 0 };
}

/**
 * Enrich a submission row with the derived flags the view + counts both read —
 * one function so a badge can never disagree with the tab it lists under.
 * @param {any} sub raw submission
 * @param {Set<string>} dupKeys dupPhoneKeys(rows) result
 * @param {any[]} patients existing patients for the cross-table dup check
 * @param {Date} now
 * @returns {any} a NEW row with {statusLabel, pending, overdue, noPdf, dupInList, crossDup} folded in
 */
export function enrichSubmission(sub, dupKeys, patients, now) {
  const pending = isPending(sub);
  const key = phoneKey(sub && sub.patient_phone);
  return Object.assign({}, sub, {
    statusLabel: normalizeStatus(sub && sub.status),
    pending,
    overdue: isOverdue(sub, now),
    noPdf: hasNoPdf(sub),
    dupInList: pending && !!key && dupKeys.has(key),
    crossDup: pending ? checkDuplicate(sub, patients) : null,
  });
}

/**
 * Classify the whole submission set: returns enriched rows + the five tab
 * counts (legacy tic-new/ov/dup/nopdf/pr loop). Pure.
 * @param {any[]} rows raw submissions
 * @param {any[]} patients existing patients for cross-table dup detection
 * @param {Date} now
 * @returns {{rows: any[], counts: {new: number, overdue: number, dup: number, nopdf: number, processed: number}}}
 */
export function classifyIntake(rows, patients, now) {
  const list = rows || [];
  const dupKeys = dupPhoneKeys(list);
  const enriched = list.map((s) => enrichSubmission(s, dupKeys, patients || [], now));
  const counts = emptyIntakeCounts();
  for (const r of enriched) {
    if (!r.pending) {
      counts.processed++;
      continue;
    }
    if (r.overdue) counts.overdue++;
    else counts.new++;
    if (r.dupInList) counts.dup++;
    if (r.noPdf) counts.nopdf++;
  }
  return { rows: enriched, counts };
}

/**
 * Rows visible under a tab (legacy renderIntake filter). Pure — operates on the
 * ENRICHED rows so it never re-derives the flags.
 * @param {any[]} enriched classifyIntake().rows
 * @param {string} tab one of INTAKE_TABS ids
 * @returns {any[]}
 */
export function rowsForIntakeTab(enriched, tab) {
  const rows = enriched || [];
  if (tab === 'new') return rows.filter((r) => r.pending && !r.overdue);
  if (tab === 'overdue') return rows.filter((r) => r.pending && r.overdue);
  if (tab === 'duplicates') return rows.filter((r) => r.pending && r.dupInList);
  if (tab === 'nopdf') return rows.filter((r) => r.pending && r.noPdf);
  if (tab === 'processed') return rows.filter((r) => !r.pending);
  return rows;
}

// ─── Promote-result parsing + honest toast copy (R-009 / Q1) ────────────────

/**
 * Parse the promote_consent_to_case result HONESTLY (legacy 5484-5489). The
 * new server shape is jsonb {case_id, created, matched_existing?, already_promoted?};
 * a legacy deployment returns a bare uuid STRING — in which case we do NOT guess
 * created-vs-matched (the client cache is programme-scoped/stale while the server
 * dedupes across ALL programmes), staying neutral.
 * @param {any} data the RPC result
 * @returns {{caseId: string|null, created: boolean, matched: boolean, already: boolean}}
 */
export function parsePromoteResult(data) {
  if (data && typeof data === 'object') {
    return {
      caseId: data.case_id || null,
      created: !!data.created,
      matched: !!data.matched_existing,
      already: !!data.already_promoted,
    };
  }
  if (typeof data === 'string' && data) {
    return { caseId: data, created: false, matched: false, already: false };
  }
  return { caseId: null, created: false, matched: false, already: false };
}

/**
 * The honest per-outcome toast for a completed promote (legacy 5502-5511). Every
 * partial outcome is voiced truthfully: a dedupe MATCH whose owner-assign then
 * network-failed did NOT create anyone; a claim-safe TAKEN is INFO (an owner
 * already exists by design), not an error; a real assign failure is a STICKY
 * error because the patient is genuinely unowned. Pure.
 * @param {{created: boolean, matched: boolean, already: boolean, caseId: string|null,
 *   assign: 'ok'|'taken'|'fail'|'none', toSelf: boolean, ownerName: string|null, takenName: string|null}} o
 * @returns {{msg: string, kind: '' | 'ok' | 'err', sticky: boolean}}
 */
export function promoteToast(o) {
  const assignedSuffix =
    o.assign === 'ok' ? (o.toSelf ? ' — added to your queue' : ' — assigned to ' + (o.ownerName || 'the nurse')) : '';
  if (o.already) {
    return {
      msg: o.caseId ? 'This consent was already accepted — opening the patient' : 'This consent was already accepted (no linked case found)',
      kind: 'ok',
      sticky: false,
    };
  }
  if (o.assign === 'fail') {
    const lead = o.matched ? 'Matched an EXISTING patient' : 'Patient created';
    return { msg: lead + ' but not yet assigned — assign from the Patients list', kind: 'err', sticky: true };
  }
  if (o.assign === 'taken') {
    return {
      msg: o.takenName ? `Already in ${o.takenName}'s queue` : 'Already in a colleague’s queue',
      kind: '',
      sticky: false,
    };
  }
  if (o.matched) {
    return { msg: 'Matched an EXISTING patient (same phone or ID)' + assignedSuffix + ' — opening their record', kind: 'ok', sticky: false };
  }
  if (o.created) {
    return {
      msg: o.assign === 'ok' ? (o.toSelf ? 'Patient added to your queue' : 'Patient added — assigned to ' + (o.ownerName || 'the nurse')) : 'New patient created — opening their record',
      kind: 'ok',
      sticky: false,
    };
  }
  // Neutral (legacy bare-uuid path): promoted, don't claim created vs matched.
  return {
    msg: o.caseId ? (o.assign === 'ok' ? (o.toSelf ? 'Patient added to your queue' : 'Patient added — assigned to ' + (o.ownerName || 'the nurse')) : 'Consent promoted — opening the patient record') : 'Consent promoted',
    kind: 'ok',
    sticky: false,
  };
}

// ─── Owner picker model (Q1 owner-at-accept ruling) ─────────────────────────

/**
 * Can this role OWN patients? Nurses + the nurse-coordinator hold caseloads;
 * admins / programme-managers never do (legacy _canOwn: !/admin|programmes/).
 * @param {any} role
 * @returns {boolean}
 */
export function canOwnPatients(role) {
  return !/admin|programmes/i.test(String(role || ''));
}

/**
 * Build the owner-picker option model (legacy _intakeOwnerSelectHTML). A nurse /
 * coordinator is pre-selected to SELF ("You (…)"); an admin gets NO default (they
 * cannot own) — Accept stays blocked until they pick. Pure — the view renders it.
 * @param {any[]} assignable assignableUsers() list
 * @param {any} selfId signed-in user id
 * @param {boolean} canOwn canOwnPatients(role)
 * @returns {{options: Array<{uid: any, label: string, selected: boolean}>, canOwn: boolean, defaultUid: any}}
 */
export function ownerPickerModel(assignable, selfId, canOwn) {
  let defaultUid = null;
  const options = (assignable || []).map((u) => {
    const self = !!canOwn && u.user_id === selfId;
    if (self) defaultUid = u.user_id;
    return {
      uid: u.user_id,
      label: self ? 'You (' + (u.full_name || '—') + ')' : (u.full_name || '—') + ' (' + (u.role || 'nurse') + ')',
      selected: self,
    };
  });
  return { options, canOwn: !!canOwn, defaultUid };
}

// ─── The loader ──────────────────────────────────────────────────────────────

/**
 * Load the consent-intake queue and publish SLICES.INTAKE. Never throws — a
 * failure lands as loadError on the slice so the view shows "couldn't load —
 * retry", never a false empty queue.
 *
 * Reads the consent_submission VIEW (programme-filtered on programme_id, newest
 * first, complete past the 1,000-row cap) and a LIGHT patients projection from
 * cases for the cross-table duplicate check (legacy loaded the full patient list
 * first for exactly this). A failed patients read only disables cross-table dup
 * badges — it never blocks the queue.
 *
 * Slice shape: { rows, counts, loadError }
 *  - rows: enriched submissions (statusLabel/pending/overdue/noPdf/dupInList/crossDup)
 *  - counts: {new, overdue, dup, nopdf, processed}
 *
 * @param {{client?: any}} [deps] test injection
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function loadIntake(deps) {
  const client = (deps && deps.client) || getClient();
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const active = (store.get(SLICES.FILTERS) || {}).programme || 'all';

  const res = await fetchAll(() =>
    applyProgrammeFilter(
      // Soft-deleted submissions excluded for the same reason as cases (see the
      // note in data/cases.js): RLS keeps them visible to admins for recovery, so
      // a removed test submission would otherwise sit in the live intake queue.
      client.from(TABLES.CONSENT_SUBMISSION).select('*', { count: 'exact' }).is('deleted_at', null),
      'programme_id',
      active,
      prog.byCode || {}
    ).order('created_at', { ascending: false })
  );
  // `loaded` marks a RESOLVED load (success or failure); the view shows a
  // skeleton until then rather than "Queue clear" over an in-flight request.
  if (!res.ok) {
    store.set(SLICES.INTAKE, { rows: [], counts: emptyIntakeCounts(), loadError: res.error, loaded: true });
    // No number after a failed reload (R-13's rule, applied here too): a stale
    // "New consents 5" beside a queue that could not load is a claim the app
    // cannot back. countBadge() hides a non-number.
    const c = store.get(SLICES.COUNTS) || {};
    store.set(SLICES.COUNTS, Object.assign({}, c, { consents: null }));
    return fail(res.error);
  }

  // Light patients read for cross-table duplicate detection. Fail-soft: a miss
  // just drops the cross-table dup badges (never blocks the queue).
  /** @type {any[]} */
  let patients = [];
  const pRes = await fetchAll(() =>
    client.from(TABLES.CASES).select('case_id,reference_number,patient_name,patient_phone,national_id', { count: 'exact' })
  );
  if (pRes.ok) patients = pRes.data;

  const { rows, counts } = classifyIntake(res.data, patients, new Date());
  store.set(SLICES.INTAKE, { rows, counts, loadError: null, loaded: true });

  // Sidebar 'consents' badge = the actionable pending items (New + Overdue),
  // matching legacy cnt-i = cn + co.
  const cnt = store.get(SLICES.COUNTS) || {};
  store.set(SLICES.COUNTS, Object.assign({}, cnt, { consents: counts.new + counts.overdue }));

  return ok({ rows, counts });
}

/**
 * Sanitize a name into a storage-safe filename stem (legacy uploadConsentFile).
 * @param {any} name
 * @returns {string}
 */
export function safeFileStem(name) {
  return String(name || 'consent').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 30);
}

/**
 * Upload a scanned consent to the private consent-forms bucket, then point the
 * consent row at the stored PATH and record a documents row (legacy
 * uploadConsentFile). Returns the envelope with data.path on success (plus
 * data.pointerFailed=true when the file stored but the consent-row pointer UPDATE
 * failed — the caller must warn so the link can be re-attached). NEVER throws — a
 * storage/DB failure is reported so the promote flow can offer the "promote
 * without it?" confirm instead of silently proceeding.
 * @param {any} file a File/Blob with .name + .type
 * @param {string} consentId the consent_id (== submission_id on the view)
 * @param {{name?: string, caseId?: string|null, client?: any}} [opts]
 * @returns {Promise<{ok: boolean, data: any, error: any, code: string|null}>}
 */
export async function uploadConsentFile(file, consentId, opts) {
  if (!file || !consentId) return fail(new Error('missing file or consent id'), 'NO_FILE');
  const client = (opts && opts.client) || getClient();
  try {
    const ext = (String(file.name || 'file.pdf').split('.').pop() || 'pdf').toLowerCase();
    const path = `${consentId}/${safeFileStem(opts && opts.name)}_${Date.now()}.${ext}`;
    const up = await client.storage.from(BUCKETS.CONSENT_FORMS).upload(path, file, {
      contentType: file.type || 'application/octet-stream',
      upsert: true,
    });
    if (up && up.error) return fail(up.error);

    // Private bucket → store the PATH (viewers get a short-lived signed URL).
    // The file is safely in the bucket; if the pointer UPDATE fails (RLS/network)
    // the consent row keeps a null signed_pdf_url, so surface it to the caller
    // (pointerFailed) rather than reporting a false all-clear — the nurse needs to
    // know the link didn't land so they can re-attach (legacy v20 surfaced this).
    const ptr = await client.from(TABLES.CONSENTS).update({ signed_pdf_url: path, updated_at: new Date().toISOString() }).eq('consent_id', consentId);
    const pointerFailed = !!(ptr && ptr.error);
    if (pointerFailed) console.warn('consent pdf pointer update failed', ptr.error);

    try {
      await client.from(TABLES.DOCUMENTS).insert([
        {
          consent_id: consentId,
          case_id: (opts && opts.caseId) || null,
          document_scope: 'consent_form',
          bucket_name: BUCKETS.CONSENT_FORMS,
          file_path: path,
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          notes: 'Physical consent form upload',
        },
      ]);
    } catch (e) {
      console.warn('documents insert failed', e);
    }
    return ok({ path, pointerFailed });
  } catch (e) {
    return fail(e);
  }
}

/**
 * Fetch a single submission by id (cache first, then the view) — used by the
 * review modal when the row isn't in the current slice (deep-link / stale).
 * @param {string} id submission_id
 * @param {{client?: any}} [deps]
 * @returns {Promise<any|null>}
 */
export async function fetchSubmission(id, deps) {
  const slice = store.get(SLICES.INTAKE) || {};
  const cached = (slice.rows || []).find((s) => s.submission_id === id || s.id === id);
  if (cached) return cached;
  const client = (deps && deps.client) || getClient();
  try {
    const { data } = await client.from(TABLES.CONSENT_SUBMISSION).select('*').eq('submission_id', id).maybeSingle();
    return data || null;
  } catch {
    return null;
  }
}
